import { useCallback, useEffect, useRef, useState } from 'react';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { useGetWorkspaceAuditEventsCallback } from '@cbhq/identity-api-hooks/account-management/fetch';

import { logError } from '../../../utils/logging';
import type { AuditLogRowEntry } from '../types';
import { mapAuditEventToRow } from '../utils/helpers';

const PAGE_SIZE = 50;

// Server-side filters applied to every page request
export type AuditEventFilters = {
  actorId: string;
  eventType: string;
  startTime: string;
  search: string;
};

const EMPTY_FILTERS: AuditEventFilters = {
  actorId: '',
  eventType: '',
  startTime: '',
  search: '',
};

export type UsePaginatedAuditEventsResult = {
  auditLogs: AuditLogRowEntry[];
  isInitialLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  loadMore: () => void;
};

export function usePaginatedAuditEvents(
  organizationId: string,
  filters: AuditEventFilters = EMPTY_FILTERS,
): UsePaginatedAuditEventsResult {
  const fetchAuditEvents = useGetWorkspaceAuditEventsCallback();

  const { actorId, eventType, startTime, search } = filters;

  const [auditLogs, setAuditLogs] = useState<AuditLogRowEntry[]>([]);
  const [nextCursor, setNextCursor] = useState('');
  const [hasMore, setHasMore] = useState(false);
  // Start in the loading state when an org is present so the first paint shows
  // the skeleton rows instead of a momentarily empty table.
  const [isLoading, setIsLoading] = useState(() => organizationId.length > 0);
  const [error, setError] = useState<Error | null>(null);

  // Synchronous guard against overlapping fetches; the token invalidates
  // in-flight responses after an organization switch.
  const isFetchingRef = useRef(false);
  const requestTokenRef = useRef(0);

  const fetchPage = useCallback(
    async (cursor: string) => {
      if (!organizationId || isFetchingRef.current) return;

      isFetchingRef.current = true;
      setIsLoading(true);

      const token = requestTokenRef.current;

      try {
        const response = await fetchAuditEvents({
          organizationId,
          size: PAGE_SIZE,
          ...(cursor ? { cursor } : {}),
          ...(actorId ? { actorId } : {}),
          ...(eventType ? { eventType } : {}),
          ...(startTime ? { startTime } : {}),
          ...(search ? { search } : {}),
        });

        if (token !== requestTokenRef.current) return;

        if (response instanceof Error) {
          logError(response, { context: 'usePaginatedAuditEvents' });
          logEvent('activity_audit_events_fetch_failed', {
            componentType: ComponentType.api,
            action: ActionType.measurement,
          });
          setError(response);
          return;
        }

        const cursorNext = response?.pageDescription?.nextCursor ?? '';
        const newRows = (response?.events ?? []).map(mapAuditEventToRow);
        setAuditLogs((prev) => {
          const seen = new Set(prev.map((row) => row.event_id));
          return [...prev, ...newRows.filter((row) => !seen.has(row.event_id))];
        });
        setNextCursor(cursorNext);
        setHasMore(cursorNext.length > 0);
        setError(null);
      } catch (caughtError) {
        if (token !== requestTokenRef.current) return;
        const errorNext =
          caughtError instanceof Error ? caughtError : new Error(String(caughtError));
        logError(errorNext, { context: 'usePaginatedAuditEvents' });
        logEvent('activity_audit_events_fetch_failed', {
          componentType: ComponentType.api,
          action: ActionType.measurement,
        });
        setError(errorNext);
      } finally {
        if (token === requestTokenRef.current) {
          isFetchingRef.current = false;
          setIsLoading(false);
        }
      }
    },
    [fetchAuditEvents, organizationId, actorId, eventType, startTime, search],
  );

  // Reset and load the first page whenever the organization or any filter changes.
  useEffect(() => {
    requestTokenRef.current += 1;
    isFetchingRef.current = false;
    setAuditLogs([]);
    setNextCursor('');
    setError(null);

    if (!organizationId) {
      setHasMore(false);
      setIsLoading(false);
      return;
    }

    setHasMore(false);
    void fetchPage('');
  }, [organizationId, fetchPage]);

  const loadMore = useCallback(() => {
    if (!hasMore || isFetchingRef.current) return;
    logEvent('activity_load_more_triggered', {
      componentType: ComponentType.button,
      action: ActionType.click,
    });
    void fetchPage(nextCursor);
  }, [hasMore, nextCursor, fetchPage]);

  return {
    auditLogs,
    isInitialLoading: isLoading && auditLogs.length === 0,
    isLoadingMore: isLoading && auditLogs.length > 0,
    hasMore,
    error,
    loadMore,
  };
}
